import React, { FC } from 'react'
import { Box, makeStyles } from 'transfix-ui/core'
import { TrackingOverview, useTrackingOverview } from 'apps/marketplace/apiTypes/shipments'
import {
  ContextCard,
  TContextCardRenderType,
  IPopperOptions,
  ITrackingProps,
  TrackingTag,
} from 'transfix-ui'
import { TrackingDetails } from './TrackingDetails'
import {
  ITrackingContextCardQuery,
  useTrackingContextCardQuery,
} from './graphql/TrackingContextCard'

interface ITrackingContextCardProps extends ITrackingProps {
  shipmentId: number
  /**
   * Set the render config values for usePopper
   * @default 'default'
   */
  renderType?: TContextCardRenderType
  /**
   * This defines the different values you can set up depending on the renderType
   */
  popperOptions?: IPopperOptions
  /**
   * This specifies that you want to use GraphQL instead of REST
   */
  useGraphQl?: boolean
}

interface ITrackingDetailsRESTInfoProps extends ITrackingProps {
  shipmentId: number
}

const useTrackingContextCardStyles = makeStyles({
  root: {
    cursor: 'auto',
  },
})

const TrackingDetailsRESTInfo: FC<ITrackingDetailsRESTInfoProps> = ({
  shipmentId,
  shipperTrackingSegment,
  trackingStatus,
}) => {
  const { data: trackingOverview, loading } = useTrackingOverview({ shipmentId })

  return (
    <TrackingDetails
      trackingOverview={trackingOverview}
      shipperTrackingSegment={shipperTrackingSegment}
      trackingStatus={trackingStatus}
      shipmentId={shipmentId}
      isLoading={loading}
    />
  )
}

const graphQlResponseToRest = ({
  trackingOverview,
}: ITrackingContextCardQuery): TrackingOverview => {
  const {
    calculatedTrackingMethod: gqlCalculatedTrackingMethod,
    calculatedTrackingMethodLatestPing,
    carrierApplicationId,
    dispatcher: gqlDispatcher,
    driver: gqlDriver,
    driverLatestPing,
    eldTruckNumber,
    trackingCapabilities: gqlTrackingCapabilities,
    trackingRequirement: gqlTrackingRequirement,
  } = trackingOverview
  const carrier = carrierApplicationId ? { id: Number(carrierApplicationId) } : undefined
  const dispatcher = gqlDispatcher
    ? {
        name: gqlDispatcher.fullName || undefined,
        email: gqlDispatcher.email || undefined,
        parentUserId: gqlDispatcher.parentUserId ? Number(gqlDispatcher.parentUserId) : undefined,
        phone: gqlDispatcher.phone || undefined,
      }
    : undefined
  const truckNumber = eldTruckNumber || undefined
  const calculatedTrackingMethod = calculatedTrackingMethodLatestPing
    ? {
        value: calculatedTrackingMethodLatestPing.sourceType,
        lastPingAt: calculatedTrackingMethodLatestPing.phoneTimestamp,
        isDrivingTracking: calculatedTrackingMethodLatestPing.isRecent,
        driverLocationTimezone: calculatedTrackingMethodLatestPing.timezone,
      }
    : undefined
  const driver =
    gqlDriver && driverLatestPing
      ? {
          parentUserId: gqlDriver.parentUserId ? Number(gqlDriver.parentUserId) : undefined,
          name: gqlDriver.fullName || undefined,
          phone: gqlDriver.cellPhone || undefined,
          lastPingAt: driverLatestPing.phoneTimestamp,
          isDriverTracking: driverLatestPing.isRecent,
          driverLocationTimezone: driverLatestPing.timezone,
        }
      : undefined
  const trackingRequirement = gqlTrackingRequirement ? Number(gqlTrackingRequirement) : undefined
  const selectedTrackingMethod = gqlCalculatedTrackingMethod || undefined
  const trackingCapabilities = gqlTrackingCapabilities || undefined

  return {
    carrier,
    dispatcher,
    truckNumber,
    calculatedTrackingMethod,
    driver,
    trackingRequirement,
    selectedTrackingMethod,
    trackingCapabilities,
  }
}

export const TrackingDetailsGraphQLInfo: FC<ITrackingDetailsRESTInfoProps> = ({
  shipmentId,
  shipperTrackingSegment,
  trackingStatus,
}) => {
  const { data, loading } = useTrackingContextCardQuery({
    variables: { id: shipmentId.toString() },
  })

  const trackingOverview = data ? graphQlResponseToRest(data) : null

  return (
    <TrackingDetails
      trackingOverview={trackingOverview}
      shipperTrackingSegment={shipperTrackingSegment}
      trackingStatus={trackingStatus}
      shipmentId={shipmentId}
      isLoading={loading}
    />
  )
}

export const TrackingContextCard: FC<ITrackingContextCardProps> = ({
  popperOptions,
  renderType,
  shipmentId,
  shipperTrackingSegment,
  trackingStatus,
  useGraphQl,
}) => {
  const classes = useTrackingContextCardStyles()

  const TrackingDetailsComponent = useGraphQl ? TrackingDetailsGraphQLInfo : TrackingDetailsRESTInfo

  return (
    <Box className={classes.root}>
      <ContextCard
        renderType={renderType}
        popperOptions={popperOptions}
        triggerText={
          <TrackingTag
            trackingStatus={trackingStatus}
            shipperTrackingSegment={shipperTrackingSegment}
            isContextCard
          />
        }>
        <TrackingDetailsComponent
          shipmentId={shipmentId}
          shipperTrackingSegment={shipperTrackingSegment}
          trackingStatus={trackingStatus}
        />
      </ContextCard>
    </Box>
  )
}
